/**
 * Static furniture / floor / wall sprite registry.
 * Paths under public/assets/sprites — served as /assets/sprites/...
 */

import {
  type DoorWall,
  MAP_FURNITURE_KINDS,
  type MapFurnitureKind,
} from "@/lib/office/personal-office-layout";
import type {
  FurnitureSpriteDef,
  SpriteCatalog,
  SpriteVariant,
  TileSpriteDef,
} from "@/lib/office/sprites/types";
import { OFFICE_UNLOCK_IDS } from "@/lib/office/unlocks";

const DIRS: readonly DoorWall[] = ["N", "S", "E", "W"];

const S1 = { width: 64, height: 72 } as const;
const S_TABLE = { width: 104, height: 56 } as const;
const S_COUNTER = { width: 152, height: 64 } as const;
const S_CONF = { width: 280, height: 96 } as const;
const S_SCREEN = { width: 72, height: 96 } as const;

const ANCHOR_DEFAULT = { ax: 0, ay: 6 } as const;
const ANCHOR_TALL = { ax: 0, ay: 4 } as const;
const ANCHOR_FLAT = { ax: 0, ay: 8 } as const;

/** Kinds that need facing variants (chair backs, desks, sofas). */
const DIR_KINDS = new Set<MapFurnitureKind>([
  "desk",
  "chair",
  "couch",
  "sofa1",
  "stool",
  "whiteboard",
]);

type KindMeta = {
  footprint?: { w: number; d: number };
  seat?: boolean;
  block?: boolean;
  useSprite?: boolean;
  tall?: boolean;
  flat?: boolean;
};

/**
 * Only kinds with real Habbo PNGs (~4–8KB) keep useSprite. Near-empty
 * placeholders (~200–600B) must stay procedural so HQ + OfficePreview don't
 * draw invisible / gray billboards.
 */
const KIND_META: Partial<Record<MapFurnitureKind, KindMeta>> = {
  desk: { block: true },
  chair: { seat: true },
  plant: { block: true },
  // Lounge heroes (real Habbo-quality PNGs — useSprite on)
  couch: { seat: true, useSprite: true },
  sofa1: { seat: true, useSprite: true },
  table: { footprint: { w: 2, d: 1 }, block: true, useSprite: false, flat: true },
  counter: { footprint: { w: 3, d: 1 }, block: true, useSprite: false, flat: true },
  cooler: { block: true, tall: true },
  conftable: { footprint: { w: 6, d: 2 }, block: true, useSprite: false, flat: true },
  screen: { footprint: { w: 2, d: 1 }, block: true, useSprite: false, tall: true },
  podium: { block: true, useSprite: false },
  cushion: { seat: true, flat: true, useSprite: false },
  fox: { block: true },
  arcade: { block: true, tall: true },
  trophy: { block: true, useSprite: false },
  sign: { tall: true, useSprite: false },
  pod: { seat: true, useSprite: false },
  lamp: { block: true, tall: true },
  cactus: { block: true, useSprite: false },
  bookshelf: { block: true, tall: true },
  beanbag: { seat: true, flat: true, useSprite: false },
  whiteboard: { block: true, tall: true, useSprite: false },
  coffee: { block: true, useSprite: false },
  printer: { block: true, useSprite: false },
  bin: { block: true, useSprite: false },
  clock: { tall: true, useSprite: false },
  fridge: { block: true, tall: true, useSprite: false },
  locker: { block: true, tall: true, useSprite: false },
  guitar: { block: true, useSprite: false },
  bike: { block: true, useSprite: false },
  fishbowl: { block: true, useSprite: false },
  easel: { block: true, tall: true, useSprite: false },
  flag: { tall: true, useSprite: false },
  neon: { tall: true },
  mic: { block: true, useSprite: false },
  stool: { seat: true, useSprite: false },
  jukebox: { block: true, tall: true },
  laptop: { block: true, useSprite: false },
  server: { block: true, tall: true, useSprite: false },
  // Flat ground covers must stay procedural — billboard PNGs read as gray boxes zoomed out.
  mat: { flat: true, useSprite: false },
  statue: { block: true, tall: true, useSprite: false },
  rug: { flat: true, useSprite: false },
  // Layout redesign heroes
  reception: { block: true, tall: true, useSprite: true },
  coffeetable: { block: true, useSprite: true },
  // Structural (campus shells + personal office walls) — always procedural
  wall: { block: true, tall: true, useSprite: false },
  gwall: { block: true, tall: true, useSprite: false },
  gwallW: { block: true, tall: true, useSprite: false },
  door: { useSprite: false },
  // Outdoor plaza — building facades use large style variants (see buildEntry override)
  building: { block: true, tall: true, useSprite: true },
  tree: { block: true, tall: true, useSprite: true },
  bush: { block: true, useSprite: true },
  bench: { seat: true, useSprite: true },
  streetlamp: { block: true, tall: true, useSprite: true },
  fountain: { block: true, tall: true, useSprite: true },
  plaza_sign: { block: true, tall: true, useSprite: true },
  delivery_truck: { block: true, footprint: { w: 3, d: 2 }, useSprite: true },
  cafe_umbrella: { block: true, tall: true, useSprite: true },
  pine: { block: true, tall: true, useSprite: true },
  rock: { block: true, useSprite: true },
  flower: { block: true, useSprite: true },
  hedge: { block: true, useSprite: true },
  planter: { block: true, useSprite: true },
  bollard: { block: true, tall: true, useSprite: true },
  fence: { block: true, useSprite: true },
  flowerbed: { block: true, footprint: { w: 2, d: 1 }, useSprite: true },
};

/** Outdoor building facade styles → large billboard PNGs (processed separately). */
/** Pixel sizes from `bun scripts/process-building-facades.ts` (tight crop). */
/** nymbl_hq is procedurally-baked pixel art from `bun scripts/gen-nymbl-hq.ts`. */
const BUILDING_STYLES = [
  { style: "cafe", width: 188, height: 202 },
  { style: "people_marketing", width: 196, height: 204 },
  { style: "delivery", width: 277, height: 220 },
  { style: "post_delivery", width: 181, height: 216 },
  { style: "customer_success", width: 243, height: 223 },
  { style: "reception", width: 284, height: 282 },
  { style: "nymbl_hq", width: 376, height: 376 },
] as const;

/**
 * Cache-busting version for sprite asset URLs. Sprite PNGs are served at stable
 * paths with a long browser cache, so when the art is re-baked/cleaned a browser
 * keeps serving the OLD cached image. Bump this whenever the shipped sprite art
 * changes — the new `?v=` makes every client fetch the fresh image on next load.
 */
const SPRITE_ASSET_VERSION = "4";
function withV(path: string): string {
  return `${path}?v=${SPRITE_ASSET_VERSION}`;
}

function furniturePath(file: string): string {
  return withV(`/assets/sprites/furniture/${file}`);
}

function thumbPath(kind: string): string {
  return withV(`/assets/sprites/thumbs/${kind}.png`);
}

function floorSrc(id: string): string {
  return withV(`/assets/sprites/floor/${id}.png`);
}

function wallSrc(id: string): string {
  return withV(`/assets/sprites/wall/${id}.png`);
}

function fourDirVariants(
  kind: string,
  size: { width: number; height: number },
  anchor: { ax: number; ay: number },
): SpriteVariant[] {
  return DIRS.map((dir) => ({
    src: furniturePath(`${kind}_${dir.toLowerCase()}.png`),
    width: size.width,
    height: size.height,
    anchor: { ax: anchor.ax, ay: anchor.ay },
    dirs: [dir],
  }));
}

function singleVariant(
  kind: string,
  size: { width: number; height: number },
  anchor: { ax: number; ay: number },
): SpriteVariant[] {
  return [
    {
      src: furniturePath(`${kind}.png`),
      width: size.width,
      height: size.height,
      anchor: { ax: anchor.ax, ay: anchor.ay },
    },
  ];
}

function sizeFor(kind: MapFurnitureKind): { width: number; height: number } {
  if (kind === "table") return S_TABLE;
  if (kind === "counter") return S_COUNTER;
  if (kind === "conftable") return S_CONF;
  if (kind === "screen") return S_SCREEN;
  return S1;
}

function buildingVariants(): SpriteVariant[] {
  return BUILDING_STYLES.map((b) => ({
    src: furniturePath(`bldg_${b.style}.png`),
    width: b.width,
    height: b.height,
    anchor: { ax: 0, ay: 4 },
    styles: [b.style],
  }));
}

function buildEntry(kind: MapFurnitureKind): FurnitureSpriteDef {
  const meta = KIND_META[kind] ?? {};
  const footprint = meta.footprint ?? { w: 1, d: 1 };
  const multiTile = footprint.w > 1 || footprint.d > 1;
  const anchor = meta.tall ? ANCHOR_TALL : meta.flat ? ANCHOR_FLAT : ANCHOR_DEFAULT;
  const size = sizeFor(kind);
  // Multi-tile outdoor buildings: style variants + useSprite (engine scales by footprint).
  // Indoor multi-tile (table/conftable) stay procedural-only.
  if (kind === "building") {
    return {
      kind,
      footprint,
      depthBias: 8,
      defaultSeat: meta.seat,
      defaultBlock: meta.block ?? true,
      useSprite: true,
      thumbSrc: thumbPath("building"),
      variants: buildingVariants(),
      proceduralFallback: true,
    };
  }
  const variants = DIR_KINDS.has(kind)
    ? fourDirVariants(kind, size, anchor)
    : singleVariant(kind, size, anchor);

  return {
    kind,
    footprint,
    defaultSeat: meta.seat,
    defaultBlock: meta.block,
    useSprite: meta.useSprite ?? !multiTile,
    thumbSrc: thumbPath(kind),
    variants,
    proceduralFallback: true,
  };
}

function buildAll(): Record<MapFurnitureKind, FurnitureSpriteDef> {
  const out = {} as Record<MapFurnitureKind, FurnitureSpriteDef>;
  for (const kind of MAP_FURNITURE_KINDS) {
    out[kind] = buildEntry(kind);
  }
  return out;
}

export const FURNITURE_SPRITES: Readonly<Record<MapFurnitureKind, FurnitureSpriteDef>> = buildAll();

// id === filename; indoor tiles + outdoor campus ground (all 64×32 iso diamonds).
const FLOOR_TILE_IDS = [
  "tile_a",
  "tile_b",
  "rug_office",
  "grass",
  "cobble",
  "cobble_round",
  "cobble_sand",
  "brick",
  "brick_dark",
  "sidewalk",
  "pavement",
  "dirt",
  "wood_deck",
] as const;

export const FLOOR_TILES: Readonly<Record<string, TileSpriteDef>> = Object.fromEntries(
  FLOOR_TILE_IDS.map((id): [string, TileSpriteDef] => [
    id,
    { id, src: floorSrc(id), width: 64, height: 32 },
  ]),
);

/**
 * Zone rug hex → outdoor floor texture id.
 * Map designer outdoor zone templates use these colors.
 */
export const OUTDOOR_FLOOR_BY_RUG: Readonly<Record<string, string>> = {
  "#B4DC9C": "grass",
  "#9FCF88": "grass",
  "#A8C898": "grass",
  "#C6D96A": "grass",
  "#A8C050": "grass",
  "#B8BCC6": "cobble",
  "#C8CCD4": "cobble",
  "#9CA3B0": "cobble_round",
  "#8E95A2": "cobble_round",
  "#C4B8A0": "cobble_sand",
  "#B8AA8C": "cobble_sand",
  "#B85A42": "brick",
  "#A84A38": "brick",
  "#6B3A2E": "brick_dark",
  "#5A3028": "brick_dark",
  "#A8ACB8": "pavement",
  "#9AA0AC": "pavement",
  "#D0D4DC": "sidewalk",
  "#D8DCE4": "sidewalk",
  "#C4A574": "wood_deck",
  "#B89560": "wood_deck",
  "#A08050": "dirt",
  "#8B6F47": "dirt",
};

export const WALL_TILES: Readonly<Record<string, TileSpriteDef>> = Object.fromEntries(
  (["outer", "glass"] as const).map((id): [string, TileSpriteDef] => [
    id,
    { id, src: wallSrc(id), width: 64, height: 80 },
  ]),
);

export const SPRITE_CATALOG: SpriteCatalog = {
  version: 1,
  furniture: FURNITURE_SPRITES,
  floor: FLOOR_TILES,
  wall: WALL_TILES,
};

/** Unlock ids that also resolve as furniture kinds (for decor placement). */
export const DECOR_SPRITE_KINDS = OFFICE_UNLOCK_IDS;
